/* eslint-disable perfectionist/sort-imports */
import 'src/utils/highlight';

import sanitizeHtml from 'sanitize-html';

import ReactQuill from 'react-quill';

import { alpha } from '@mui/material/styles';

import { useRef, useEffect, forwardRef } from 'react';
import { convertRgbToHex } from 'src/utils/html.utils';
import { StyledEditor } from './styles';
import Toolbar, { formats } from './toolbar';
import { EditorProps } from './types';

// ----------------------------------------------------------------------

const Editor = forwardRef<ReactQuill, EditorProps>(({
  id = 'minimal-quill',
  error,
  simple = false,
  noHeader = false,
  helperText,
  disabled,
  sx,
  formats: formatsProp,
  ...other
}, ref) => {
  
  const quillRef = useRef<ReactQuill>(null);

  const modules = {
    toolbar: {
      container: `#${id}`,
    },
    history: {
      delay: 500,
      maxStack: 100,
      userOnly: false,
    },
    syntax: true,
    clipboard: {
      matchVisual: false,
    },
  };

  const sanitizeClipboard = (clipboard: ClipboardEvent) => {
    clipboard.preventDefault();

    const { clipboardData } = clipboard;
    if (!clipboardData) {
      console.warn('No clipboard data available');
      return;
    }

    // First try HTML content
    if (clipboardData.types.includes('text/html')) {
      try {
        const html = clipboardData.getData('text/html');

        let sanitizedHtml = sanitizeHtml(html, {
          allowedTags: ['b', 'i', 'em', 'strong', 'a', 'p', 'br', 'div', 'span', 'ul', 'ol', 'li'],
          allowedAttributes: {
            'a': ['href']
          }
        });

        // convert rgb to hex
        sanitizedHtml = convertRgbToHex(sanitizedHtml);

        if (sanitizedHtml.trim()) {
          quillRef.current?.editor?.clipboard.dangerouslyPasteHTML(sanitizedHtml);
          return;
        }
      } catch (e) {
        console.error('Error processing HTML clipboard content:', e);
        // Fall through to plain text handling
      }
    }

    // Fallback to plain text if HTML parsing fails or produces empty content
    try {
      const text = clipboardData.getData('text/plain');
      if (text.trim()) {
        quillRef.current?.editor?.clipboard.dangerouslyPasteHTML(text);
        return;
      }
    } catch (e) {
      console.error('Error processing plain text clipboard content:', e);
    }

    // Log if no content could be pasted
    console.warn('No valid content found in clipboard');
  };

  useEffect(() => {
    if (!quillRef.current) {
      return;
    }

    const quill = quillRef.current.getEditor();

    quill.root.addEventListener('paste', sanitizeClipboard);

    // eslint-disable-next-line consistent-return
    return () => {
      quill.root.removeEventListener('paste', sanitizeClipboard);
    };
  }, []);

  return (
    <>
      <StyledEditor
        sx={{
          ...(error && {
            border: (theme) => `solid 1px ${theme.palette.error.main}`,
            '& .ql-editor': {
              bgcolor: (theme) => alpha(theme.palette.error.main, 0.08),
            },
          }),
          ...sx,
        }}
      >
        <Toolbar id={id} simple noHeader={noHeader} />

        <ReactQuill
          ref={(instance) => {
            // Update internal ref
            (quillRef as React.MutableRefObject<ReactQuill | null>).current = instance;
            // Forward ref to parent
            if (ref) {
              if (typeof ref === 'function') {
                ref(instance);
              } else {
                (ref as React.MutableRefObject<ReactQuill | null>).current = instance;
              }
            }
          }}
          modules={modules}
          formats={formatsProp ?? formats}
          readOnly={disabled}
          theme="snow"
          placeholder="Write something awesome..."
          {...other}
        />
      </StyledEditor>

      {helperText && helperText}
    </>
  );
});

Editor.displayName = 'Editor';

export default Editor;
